'use client'

import { AnimatePresence, motion, useDragControls, useWillChange } from 'motion/react'
import { Fragment, PointerEvent, useCallback, useEffect, useId, useRef } from 'react'
import { useMeasure } from 'react-use'
import { Button } from '@/components/button'
import { FadeOverlay } from '@/components/fade-overlay'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { IChildrenProps } from '@/typings/children-props'
import { cn } from '@/utils/styles/cn'
import { Z_INDEX } from '@/utils/styles/z-index'
import { useMobile } from '@/utils/window/use-mobile'

export interface ISlidingPaneProps extends IChildrenProps {
    onClose: () => void
    isOpen: boolean
    sizeType?: 'full' | 'fit'
    withAnimation?: boolean
}

const TOP_OFFSET = 40

export const SlidingPane = ({
    children,
    onClose,
    isOpen,
    sizeType = 'full',
    withAnimation = true,
}: ISlidingPaneProps) => {
    const componentId = useId()

    const isMobile = useMobile()

    const direction = isMobile ? 'bottom' : 'right'

    const backgroundRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (typeof window === 'undefined' || !isOpen) return

        const prevOverflowY = window.document.body.style.overflowY

        setTimeout(() => {
            window.document.body.style.overflowY = 'hidden'
        }, 0)

        backgroundRef.current?.addEventListener('touchmove', (e) => {
            e.preventDefault()
        })

        return () => {
            window.document.body.style.overflowY = prevOverflowY
        }
    }, [isOpen])

    const [childrenRef, { height: childrenHeight }] = useMeasure<HTMLDivElement>()

    const willChange = useWillChange()
    const slidingPaneControls = useDragControls()

    const startDrag = useCallback(
        (event: PointerEvent<Element>) => {
            slidingPaneControls.start(event)

            event.stopPropagation()
        },
        [slidingPaneControls]
    )

    const [innerContainerRef, { height: innerContainerHeight }] = useMeasure<HTMLDivElement>()

    return (
        <Fragment>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        onClick={onClose}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isOpen ? 1 : 0 }}
                        exit={{ opacity: 0 }}
                        key={`sliding-pane-background-${componentId}`}
                        transition={{ ease: 'easeInOut', duration: 0.3 }}
                        style={{ zIndex: Z_INDEX.modal - 1, willChange }}
                        className="min-h-dvh min-w-[100vw] fixed bg-black/40 top-0 left-0 touch-none"
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{
                            y: direction === 'bottom' && withAnimation ? '100%' : 0,
                            x: direction === 'right' && withAnimation ? '100%' : 0,
                            height: '100%',
                            opacity: withAnimation ? 0.7 : 1,
                        }}
                        animate={{
                            y:
                                direction === 'bottom'
                                    ? isOpen
                                        ? sizeType === 'fit'
                                            ? 0
                                            : (TOP_OFFSET ?? 0)
                                        : window.innerHeight
                                    : 0,
                            x: direction === 'right' ? (isOpen ? 0 : '100%') : 0,
                            height: innerContainerHeight ?? '100%',
                            opacity: isOpen ? 1 : 0.8,
                        }}
                        exit={{
                            y: direction === 'bottom' ? '100%' : 0,
                            x: direction === 'right' ? '100%' : 0,
                            opacity: [1, 0.9, 0.8, 0.7, 0.7, 0.7, 0.7, 0.7, 0.7, 0.7, 0.1, 0],
                            transition: {
                                y: { duration: 0.15, ease: 'easeOut' },
                                opacity: { duration: 0.16, ease: 'easeIn' },
                            },
                        }}
                        key={`sliding-pane-${componentId}`}
                        transition={{
                            opacity: {
                                ease: 'easeOut',
                                duration: 0.3,
                            },
                            height: {
                                type: 'spring',
                                duration: 0.4,
                                bounce: 0.05,
                            },
                            y: {
                                type: 'spring',
                                duration: 0.4,
                                bounce: 0.1,
                            },
                            x: {
                                type: 'spring',
                                duration: 0.4,
                                bounce: 0.1,
                            },
                        }}
                        onDragEnd={(_, info) => {
                            if (info?.offset.y > 0) {
                                onClose()
                            }
                        }}
                        dragListener={false}
                        dragControls={slidingPaneControls}
                        drag={direction === 'bottom' ? 'y' : undefined}
                        dragConstraints={{ top: 0 }}
                        draggable={direction === 'bottom'}
                        dragMomentum={false}
                        dragElastic={true}
                        style={{ willChange }}
                        className={cn(
                            'fixed max-h-dvh bg-white z-modal bottom-0',
                            direction === 'bottom'
                                ? 'min-w-[100vw] max-w-[100vw] left-0 rounded-t-[20px]'
                                : 'min-w-[800px] max-w-[100vw] right-0'
                        )}
                    >
                        <div
                            ref={innerContainerRef}
                            className={cn(
                                `rounded-t-[inherit] overflow-y-scroll flex flex-col webkit-overflow-scrolling-touch max-h-dvh
                                scrollbar-hide`,
                                sizeType === 'full' ? 'min-h-dvh' : 'min-h-fit',
                                childrenHeight > innerContainerHeight ? 'touch-all' : 'touch-none'
                            )}
                        >
                            {direction === 'bottom' && (
                                <div
                                    onPointerDown={startDrag}
                                    // CSS Touch-none is needed as otherwise, the drag does not work.
                                    className="rounded-t-[inherit] overflow-visible min-h-[80px] bg-transparent z-[1] absolute top-0 right-0 w-full
                                        touch-none"
                                >
                                    <FadeOverlay
                                        className="h-28 justify-end rotate-180 absolute top-0 w-full rounded-b-[20px] overflow-hidden"
                                        imageClassName="object-top"
                                    />

                                    <div
                                        onClick={onClose}
                                        className="rotate-90 absolute top-4 right-4"
                                    >
                                        <Button
                                            onClick={onClose}
                                            styleType="secondary"
                                            shape="circle"
                                            borderColor="transparent"
                                            className="flex items-center justify-center"
                                        >
                                            <SvgOutlinedChevronRight color="black" size={24} />
                                        </Button>
                                    </div>

                                    <div className="flex items-center justify-center absolute top-5 left-1/2 transform -translate-x-1/2">
                                        <div className="h-2 w-14 bg-black/70 rounded-[8px]" />
                                    </div>
                                </div>
                            )}

                            <div
                                className={cn(direction === 'bottom' && 'mt-[60px]')}
                                ref={childrenRef}
                            >
                                {children}
                            </div>

                            <div style={{ marginBottom: TOP_OFFSET }} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Fragment>
    )
}
